import React, { useState, useEffect } from "react";
import axios from "axios";

export default function BankAccounts() {
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [fdProducts, setFdProducts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  
  const [showFdModal, setShowFdModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  const [fdForm, setFdForm] = useState({ amount: 50000, tenorMonths: 12, accountName: "", autoRenew: false, fundFromAccountId: "" });
  const [fdCalculation, setFdCalculation] = useState(null);
  const [txnForm, setTxnForm] = useState({ accountId: 0, amount: 0, description: "", type: "deposit" });

  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: "http://localhost:5025/api",
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    loadAccounts();
    loadAccountTypes();
    loadFdProducts();
  }, []);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const [accountsRes, summaryRes] = await Promise.all([
        api.get("/BankAccount/my-accounts"),
        api.get("/BankAccount/summary")
      ]);
      setAccounts(accountsRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error("Error loading accounts:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadAccountTypes = async () => {
    try {
      const res = await api.get("/BankAccount/types");
      setAccountTypes(res.data);
    } catch (err) {
      console.error("Error loading account types:", err);
    }
  };

  const loadFdProducts = async () => {
    try {
      const res = await api.get("/BankAccount/fixed-deposit/products");
      setFdProducts(res.data);
    } catch (err) {
      console.error("Error loading FD products:", err);
    }
  };

  const loadAccountDetails = async (account) => {
    setSelectedAccount(account);
    setLoading(true);
    try {
      const res = await api.get(`/BankAccount/${account.id}/transactions?limit=20`);
      setTransactions(res.data);
      setShowDetailsModal(true);
    } catch (err) {
      console.error("Error loading transactions:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFD = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/BankAccount/fixed-deposit/open", {
        ...fdForm,
        fundFromAccountId: fdForm.fundFromAccountId ? parseInt(fdForm.fundFromAccountId) : null
      });
      alert("Fixed Deposit created successfully!");
      setShowFdModal(false);
      setFdForm({ amount: 50000, tenorMonths: 12, accountName: "", autoRenew: false, fundFromAccountId: "" });
      setFdCalculation(null);
      loadAccounts();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create fixed deposit");
    } finally {
      setLoading(false);
    }
  };

  const calculateFD = async () => {
    try {
      const res = await api.get(`/BankAccount/fixed-deposit/calculate?principal=${fdForm.amount}&tenorMonths=${fdForm.tenorMonths}`);
      setFdCalculation(res.data);
    } catch (err) {
      console.error("Error calculating FD:", err);
    }
  };

  const handleTransaction = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = txnForm.type === "deposit" ? "/BankAccount/deposit" : "/BankAccount/withdraw";
      await api.post(endpoint, {
        accountId: txnForm.accountId,
        amount: parseFloat(txnForm.amount),
        description: txnForm.description
      });
      alert(`${txnForm.type === "deposit" ? "Deposit" : "Withdrawal"} successful!`);
      setShowTransactionModal(false);
      setTxnForm({ accountId: 0, amount: 0, description: "", type: "deposit" });
      loadAccounts();
      if (showDetailsModal && selectedAccount) {
        loadAccountDetails({ ...selectedAccount });
      }
    } catch (err) {
      alert(err.response?.data?.message || "Transaction failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrimary = async (accountId) => {
    try {
      await api.post("/BankAccount/set-primary", { accountId });
      alert("Primary account updated!");
      loadAccounts();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to set primary account");
    }
  };

  const handleLiquidateFD = async (account) => {
    const msg = account.isMatured 
      ? "This Fixed Deposit has matured. Proceed with liquidation?" 
      : "This Fixed Deposit has NOT matured. Early withdrawal penalty will apply. Continue?";
    if (!window.confirm(msg)) return;
    
    setLoading(true);
    try {
      await api.post("/BankAccount/fixed-deposit/liquidate", {
        accountId: account.id,
        reason: "User requested liquidation"
      });
      alert("Fixed Deposit liquidated successfully!");
      loadAccounts();
      setShowDetailsModal(false);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to liquidate");
    } finally {
      setLoading(false);
    }
  };

  const openTxnModal = (account, type) => {
    setTxnForm({ accountId: account.id, amount: 0, description: "", type });
    setShowTransactionModal(true);
  };

  const formatCurrency = (amount) => `₦${(amount || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
  const formatDate = (date) => date ? new Date(date).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" }) : "-";

  const getAccountIcon = (type) => ({ Savings: "💰", Current: "💳", Corporate: "🏢", FixedDeposit: "🔒" }[type] || "📁");
  
  const getStatusStyle = (status) => ({
    Active: { background: "#dcfce7", color: "#16a34a" },
    Dormant: { background: "#fef3c7", color: "#d97706" },
    Frozen: { background: "#fee2e2", color: "#dc2626" },
    Closed: { background: "#e5e7eb", color: "#6b7280" }
  }[status] || { background: "#e5e7eb", color: "#6b7280" });

  const styles = {
    container: { padding: "20px", maxWidth: "1400px", margin: "0 auto", fontFamily: "Arial, sans-serif" },
    title: { fontSize: "28px", fontWeight: "bold", color: "#1a1a2e", marginBottom: "20px" },
    summaryCards: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "25px" },
    summaryCard: { background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
    summaryValue: { fontSize: "24px", fontWeight: "bold" },
    summaryLabel: { fontSize: "13px", color: "#6b7280", marginTop: "5px" },
    actionBtns: { display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" },
    btn: { padding: "12px 20px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600" },
    primaryBtn: { background: "#3b82f6", color: "white" },
    successBtn: { background: "#16a34a", color: "white" },
    warningBtn: { background: "#f59e0b", color: "white" },
    dangerBtn: { background: "#dc2626", color: "white" },
    secondaryBtn: { background: "#e5e7eb", color: "#374151" },
    accountsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "20px" },
    accountCard: { background: "white", borderRadius: "16px", padding: "20px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", border: "1px solid #e5e7eb", position: "relative" },
    accountHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "15px" },
    accountIcon: { fontSize: "32px" },
    accountType: { fontSize: "12px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "1px" },
    accountName: { fontSize: "18px", fontWeight: "600", color: "#1a1a2e", marginTop: "4px" },
    accountNumber: { fontSize: "13px", color: "#6b7280", fontFamily: "monospace" },
    badge: { padding: "4px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: "600" },
    balanceSection: { background: "#f9fafb", borderRadius: "12px", padding: "15px", marginBottom: "15px" },
    balanceLabel: { fontSize: "12px", color: "#6b7280" },
    balanceValue: { fontSize: "28px", fontWeight: "bold", color: "#1a1a2e" },
    availableBalance: { fontSize: "13px", color: "#6b7280", marginTop: "5px" },
    accountMeta: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", fontSize: "13px", marginBottom: "15px" },
    metaItem: { display: "flex", flexDirection: "column" },
    metaLabel: { color: "#6b7280", fontSize: "11px" },
    metaValue: { fontWeight: "500", color: "#374151" },
    cardActions: { display: "flex", gap: "8px", flexWrap: "wrap" },
    cardBtn: { flex: 1, padding: "10px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "500", fontSize: "13px", minWidth: "80px" },
    primaryBadge: { position: "absolute", top: "10px", right: "10px", background: "#fef3c7", color: "#d97706", padding: "4px 10px", borderRadius: "12px", fontSize: "10px", fontWeight: "600" },
    fdProgress: { marginTop: "10px" },
    progressBar: { height: "8px", background: "#e5e7eb", borderRadius: "4px", overflow: "hidden" },
    progressFill: { height: "100%", background: "#16a34a", borderRadius: "4px" },
    modalOverlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
    modal: { background: "white", borderRadius: "16px", padding: "25px", width: "500px", maxWidth: "95%", maxHeight: "90vh", overflow: "auto" },
    modalLarge: { width: "700px" },
    modalTitle: { fontSize: "20px", fontWeight: "bold", marginBottom: "20px", color: "#1a1a2e" },
    formGroup: { marginBottom: "15px" },
    label: { display: "block", fontWeight: "600", marginBottom: "6px", color: "#374151", fontSize: "14px" },
    input: { width: "100%", padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
    select: { width: "100%", padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box", background: "white" },
    modalFooter: { display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "20px" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "13px" },
    th: { padding: "12px 8px", textAlign: "left", borderBottom: "2px solid #e5e7eb", fontWeight: "600", color: "#374151", background: "#f9fafb" },
    td: { padding: "10px 8px", borderBottom: "1px solid #e5e7eb" },
    infoBox: { background: "#eff6ff", border: "1px solid #3b82f6", borderRadius: "12px", padding: "15px", marginBottom: "20px" },
    fdCalcBox: { background: "#f0fdf4", border: "1px solid #16a34a", borderRadius: "12px", padding: "15px", marginTop: "15px" }
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>🏦 My Bank Accounts</h1>

      {/* Summary Cards */}
      {summary && (
        <div style={styles.summaryCards}>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryValue, color: "#3b82f6" }}>{summary.totalAccounts}</div>
            <div style={styles.summaryLabel}>Total Accounts</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryValue, color: "#16a34a" }}>{formatCurrency(summary.totalBalance)}</div>
            <div style={styles.summaryLabel}>Total Balance</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryValue, color: "#8b5cf6" }}>{formatCurrency(summary.totalAvailableBalance)}</div>
            <div style={styles.summaryLabel}>Available Balance</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryValue, color: "#f59e0b" }}>{formatCurrency(summary.totalAccruedInterest)}</div>
            <div style={styles.summaryLabel}>Accrued Interest</div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={styles.actionBtns}>
        <button style={{ ...styles.btn, ...styles.successBtn }} onClick={() => setShowFdModal(true)}>
          🔒 Create Fixed Deposit
        </button>
        <button style={{ ...styles.btn, ...styles.secondaryBtn }} onClick={loadAccounts}>
          🔄 Refresh
        </button>
      </div>

      {/* Accounts Grid */}
      {loading ? (
        <p>Loading...</p>
      ) : accounts.length > 0 ? (
        <div style={styles.accountsGrid}>
          {accounts.map(account => (
            <div key={account.id} style={styles.accountCard}>
              {account.isPrimary && <div style={styles.primaryBadge}>⭐ PRIMARY</div>}
              
              <div style={styles.accountHeader}>
                <div>
                  <div style={styles.accountIcon}>{getAccountIcon(account.accountType)}</div>
                  <div style={styles.accountType}>{account.accountTypeDisplay}</div>
                  <div style={styles.accountName}>{account.accountName}</div>
                  <div style={styles.accountNumber}>{account.accountNumber}</div>
                </div>
                <span style={{ ...styles.badge, ...getStatusStyle(account.status) }}>{account.status}</span>
              </div>

              <div style={styles.balanceSection}>
                <div style={styles.balanceLabel}>Balance</div>
                <div style={styles.balanceValue}>{formatCurrency(account.balance)}</div>
                {account.accountType !== "FixedDeposit" && (
                  <div style={styles.availableBalance}>Available: {formatCurrency(account.availableBalance)}</div>
                )}
              </div>

              <div style={styles.accountMeta}>
                {account.interestRate > 0 && (
                  <div style={styles.metaItem}>
                    <span style={styles.metaLabel}>Interest Rate</span>
                    <span style={styles.metaValue}>{account.interestRate}% p.a.</span>
                  </div>
                )}
                {account.accountType === "FixedDeposit" ? (
                  <>
                    <div style={styles.metaItem}>
                      <span style={styles.metaLabel}>Maturity Date</span>
                      <span style={styles.metaValue}>{formatDate(account.maturityDate)}</span>
                    </div>
                    <div style={styles.metaItem}>
                      <span style={styles.metaLabel}>Maturity Amount</span>
                      <span style={{ ...styles.metaValue, color: "#16a34a" }}>{formatCurrency(account.maturityAmount)}</span>
                    </div>
                    <div style={styles.metaItem}>
                      <span style={styles.metaLabel}>Days to Maturity</span>
                      <span style={styles.metaValue}>{account.isMatured ? "Matured ✓" : `${account.daysToMaturity} days`}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={styles.metaItem}>
                      <span style={styles.metaLabel}>Daily Limit</span>
                      <span style={styles.metaValue}>{formatCurrency(account.dailyTransactionLimit)}</span>
                    </div>
                    {account.accruedInterest > 0 && (
                      <div style={styles.metaItem}>
                        <span style={styles.metaLabel}>Accrued Interest</span>
                        <span style={{ ...styles.metaValue, color: "#16a34a" }}>{formatCurrency(account.accruedInterest)}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {account.accountType === "FixedDeposit" && account.tenorMonths && (
                <div style={styles.fdProgress}>
                  <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px" }}>
                    Progress: {account.tenorMonths} months tenor
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{ 
                      ...styles.progressFill, 
                      width: `${Math.min(100, Math.max(0, 100 - (account.daysToMaturity / (account.tenorMonths * 30) * 100)))}%`,
                      background: account.isMatured ? "#16a34a" : "#3b82f6"
                    }} />
                  </div>
                </div>
              )}

              <div style={styles.cardActions}>
                <button style={{ ...styles.cardBtn, ...styles.primaryBtn }} onClick={() => loadAccountDetails(account)}>
                  View
                </button>
                {account.accountType !== "FixedDeposit" && (
                  <>
                    <button style={{ ...styles.cardBtn, ...styles.successBtn }} onClick={() => openTxnModal(account, "deposit")}>
                      Deposit
                    </button>
                    <button style={{ ...styles.cardBtn, ...styles.warningBtn }} onClick={() => openTxnModal(account, "withdraw")}>
                      Withdraw
                    </button>
                  </>
                )}
                {account.accountType === "FixedDeposit" && (
                  <button style={{ ...styles.cardBtn, ...styles.dangerBtn }} onClick={() => handleLiquidateFD(account)}>
                    {account.isMatured ? "Withdraw" : "Liquidate"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "60px", background: "white", borderRadius: "16px" }}>
          <div style={{ fontSize: "48px", marginBottom: "20px" }}>🏦</div>
          <h3>No accounts yet</h3>
          <p style={{ color: "#6b7280" }}>Visit the Account Opening page to create your first account</p>
          <button style={{ ...styles.btn, ...styles.primaryBtn, marginTop: "20px" }} onClick={() => window.location.href = "/account-opening"}>
            Go to Account Opening
          </button>
        </div>
      )}

      {/* Fixed Deposit Modal */}
      {showFdModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>🔒 Create Fixed Deposit</h3>
            <form onSubmit={handleOpenFD}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Amount</label>
                <input
                  type="number"
                  style={styles.input}
                  min="50000"
                  value={fdForm.amount}
                  onChange={(e) => setFdForm({ ...fdForm, amount: parseFloat(e.target.value) || 0 })}
                  required
                />
                <small style={{ color: "#6b7280" }}>Minimum: ₦50,000</small>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Tenor (Months)</label>
                <select
                  style={styles.select}
                  value={fdForm.tenorMonths}
                  onChange={(e) => setFdForm({ ...fdForm, tenorMonths: parseInt(e.target.value) })}
                >
                  {fdProducts.length > 0 ? (
                    fdProducts.map(p => (
                      <option key={p.id} value={p.tenorMonths}>
                        {p.tenorMonths} Months - {p.interestRate}% p.a.
                      </option>
                    ))
                  ) : (
                    <>
                      <option value={3}>3 Months - 6.0% p.a.</option>
                      <option value={6}>6 Months - 7.0% p.a.</option>
                      <option value={12}>12 Months - 8.0% p.a.</option>
                      <option value={24}>24 Months - 9.0% p.a.</option>
                      <option value={36}>36 Months - 10.0% p.a.</option>
                    </>
                  )}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Fund From Account (Optional)</label>
                <select
                  style={styles.select}
                  value={fdForm.fundFromAccountId}
                  onChange={(e) => setFdForm({ ...fdForm, fundFromAccountId: e.target.value })}
                >
                  <option value="">-- Cash Deposit --</option>
                  {accounts.filter(a => a.accountType !== "FixedDeposit" && a.status === "Active").map(a => (
                    <option key={a.id} value={a.id}>
                      {a.accountName} ({a.accountNumber}) - {formatCurrency(a.availableBalance)}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={{ ...styles.label, display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="checkbox"
                    checked={fdForm.autoRenew}
                    onChange={(e) => setFdForm({ ...fdForm, autoRenew: e.target.checked })}
                  />
                  Auto-renew at maturity
                </label>
              </div>

              <button type="button" style={{ ...styles.btn, ...styles.secondaryBtn, width: "100%" }} onClick={calculateFD}>
                Calculate Returns
              </button>

              {fdCalculation && (
                <div style={styles.fdCalcBox}>
                  <h4 style={{ margin: "0 0 10px" }}>📊 Projected Returns</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                    <div>
                      <div style={{ fontSize: "12px", color: "#6b7280" }}>Principal</div>
                      <div style={{ fontWeight: "600" }}>{formatCurrency(fdCalculation.principal)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "12px", color: "#6b7280" }}>Interest Rate</div>
                      <div style={{ fontWeight: "600" }}>{fdCalculation.interestRate}% p.a.</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "12px", color: "#6b7280" }}>Total Interest</div>
                      <div style={{ fontWeight: "600", color: "#16a34a" }}>{formatCurrency(fdCalculation.totalInterest)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "12px", color: "#6b7280" }}>Maturity Amount</div>
                      <div style={{ fontWeight: "700", fontSize: "18px", color: "#16a34a" }}>{formatCurrency(fdCalculation.maturityAmount)}</div>
                    </div>
                  </div>
                  <div style={{ marginTop: "10px", fontSize: "12px", color: "#6b7280" }}>
                    Maturity Date: {formatDate(fdCalculation.maturityDate)}
                  </div>
                </div>
              )}

              <div style={styles.modalFooter}>
                <button type="button" style={{ ...styles.btn, ...styles.secondaryBtn }} onClick={() => { setShowFdModal(false); setFdCalculation(null); }}>
                  Cancel
                </button>
                <button type="submit" style={{ ...styles.btn, ...styles.successBtn }} disabled={loading}>
                  {loading ? "Creating..." : "Create Fixed Deposit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transaction Modal */}
      {showTransactionModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>
              {txnForm.type === "deposit" ? "💵 Deposit Funds" : "💸 Withdraw Funds"}
            </h3>
            <form onSubmit={handleTransaction}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Amount</label>
                <input
                  type="number"
                  style={styles.input}
                  min="1"
                  value={txnForm.amount}
                  onChange={(e) => setTxnForm({ ...txnForm, amount: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description (Optional)</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="e.g., Salary, Bill payment"
                  value={txnForm.description}
                  onChange={(e) => setTxnForm({ ...txnForm, description: e.target.value })}
                />
              </div>

              <div style={styles.modalFooter}>
                <button type="button" style={{ ...styles.btn, ...styles.secondaryBtn }} onClick={() => setShowTransactionModal(false)}>
                  Cancel
                </button>
                <button type="submit" style={{ ...styles.btn, ...(txnForm.type === "deposit" ? styles.successBtn : styles.warningBtn) }} disabled={loading}>
                  {loading ? "Processing..." : (txnForm.type === "deposit" ? "Deposit" : "Withdraw")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Account Details Modal */}
      {showDetailsModal && selectedAccount && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, ...styles.modalLarge }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ ...styles.modalTitle, marginBottom: 0 }}>
                {getAccountIcon(selectedAccount.accountType)} {selectedAccount.accountName}
              </h3>
              <span style={{ ...styles.badge, ...getStatusStyle(selectedAccount.status) }}>{selectedAccount.status}</span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: "20px" }}>
              <div style={{ ...styles.summaryCard, margin: 0 }}>
                <div style={styles.summaryLabel}>Account Number</div>
                <div style={{ fontSize: "18px", fontWeight: "600", fontFamily: "monospace" }}>{selectedAccount.accountNumber}</div>
              </div>
              <div style={{ ...styles.summaryCard, margin: 0 }}>
                <div style={styles.summaryLabel}>Current Balance</div>
                <div style={{ fontSize: "24px", fontWeight: "bold", color: "#16a34a" }}>{formatCurrency(selectedAccount.balance)}</div>
              </div>
            </div>

            {!selectedAccount.isPrimary && selectedAccount.accountType !== "FixedDeposit" && (
              <button style={{ ...styles.btn, ...styles.secondaryBtn, marginBottom: "20px" }} onClick={() => handleSetPrimary(selectedAccount.id)}>
                ⭐ Set as Primary Account
              </button>
            )}

            <h4>Recent Transactions</h4>
            {transactions.length > 0 ? (
              <div style={{ maxHeight: "300px", overflow: "auto" }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Description</th>
                      <th style={styles.th}>Amount</th>
                      <th style={styles.th}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(txn => (
                      <tr key={txn.id}>
                        <td style={styles.td}>{formatDate(txn.createdAt)}</td>
                        <td style={styles.td}>
                          <span style={{ 
                            ...styles.badge, 
                            background: txn.transactionType.includes("Deposit") || txn.transactionType.includes("In") || txn.transactionType.includes("Interest") ? "#dcfce7" : "#fee2e2",
                            color: txn.transactionType.includes("Deposit") || txn.transactionType.includes("In") || txn.transactionType.includes("Interest") ? "#16a34a" : "#dc2626"
                          }}>
                            {txn.transactionType}
                          </span>
                        </td>
                        <td style={styles.td}>{txn.description || "-"}</td>
                        <td style={{ ...styles.td, fontWeight: "600", color: txn.transactionType.includes("Deposit") || txn.transactionType.includes("In") ? "#16a34a" : "#dc2626" }}>
                          {txn.transactionType.includes("Deposit") || txn.transactionType.includes("In") ? "+" : "-"}{formatCurrency(txn.amount)}
                        </td>
                        <td style={styles.td}>{formatCurrency(txn.balanceAfter)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: "#6b7280", textAlign: "center", padding: "20px" }}>No transactions yet</p>
            )}

            <div style={styles.modalFooter}>
              <button style={{ ...styles.btn, ...styles.secondaryBtn }} onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

